import { getOrCreateVisitorId } from './visitor';
import { resolveApiUrl } from '@/config/apiConfig';
import { recordFetchAbort } from '@/utils/runtimeMetrics';

export type ApiOptions = RequestInit & { auth?: boolean; suppressUnauthorizedEvent?: boolean };

const isAbortError = (error: unknown): boolean => {
  if (!error) return false;
  if (error instanceof DOMException) {
    return error.name === 'AbortError';
  }
  if (typeof error === 'object' && 'name' in (error as Record<string, unknown>)) {
    return (error as { name?: unknown }).name === 'AbortError';
  }
  return false;
};

function getToken() {
  try {
    return localStorage.getItem('authToken') || undefined;
  } catch {
    return undefined;
  }
}

let lastUnauthorizedDispatch = 0;

export async function apiFetch<T = any>(input: string, init: ApiOptions = {}): Promise<T> {
  const { auth = true, suppressUnauthorizedEvent = false, headers: initHeaders, ...rest } = init;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(initHeaders || {}),
  };

  const visitorId = getOrCreateVisitorId();
  if (visitorId) {
    (headers as Record<string, string>)['X-Visitor-ID'] = visitorId;
  }

  const token = auth ? getToken() : undefined;
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const url = resolveApiUrl(input);
  let res: Response;
  try {
    res = await fetch(url, {
      ...rest,
      headers,
      credentials: rest.credentials ?? 'include',
    });
  } catch (error) {
    if (isAbortError(error)) {
      try {
        recordFetchAbort({
          input,
          resolvedUrl: url,
          method: (rest.method || 'GET').toUpperCase(),
        });
      } catch {
        // Swallow metric submission failures silently
      }
    }
    throw error;
  }
  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const body = isJson ? await res.json().catch(() => ({})) : await res.text();
  // Auto-handle unauthorized responses globally so the UI can react (logout, redirect)
  if (res.status === 401 && !suppressUnauthorizedEvent) {
    const now = Date.now();
    if (now - lastUnauthorizedDispatch > 3000) {
      lastUnauthorizedDispatch = now;
      try {
        window.dispatchEvent(
          new CustomEvent('app:unauthorized', {
            detail: { status: res.status, body },
          })
        );
      } catch {
        // no-op if window is unavailable
      }
    }
  }
  if (!res.ok) {
    const message = isJson && body?.error ? body.error : res.statusText;
    throw new Error(message || `HTTP ${res.status}`);
  }
  return body as T;
}

export type ContactRequestPayload = {
  name: string
  email: string
  organisation?: string
  message: string
  consent: boolean
}

export async function submitContactRequest(payload: ContactRequestPayload): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/api/contact`, {
    method: 'POST',
    body: JSON.stringify(payload),
    auth: false,
  })
}

// Scan Events types and helper
export type ScanEvent = {
  id: string
  scan_id: string
  user_id?: string
  org_id?: string | null
  event_type: string
  at: string
  metadata?: any
}

export async function getScanEvents(scanId: string): Promise<ScanEvent[]> {
  return apiFetch<ScanEvent[]>(`/api/scans/${encodeURIComponent(scanId)}/events`)
}

export type VerifyFindingResult = {
  ok: boolean
  label?: 'Confirmed' | 'Likely' | 'Suspected' | 'Inconclusive'
  score?: number
  confirmations?: string[]
  signals?: string[]
  diff?: any
  poc?: Array<{
    name: string
    curl: string
    expectedSignal?: {
      type?: string
      summary?: string
      metrics?: Record<string, number | string | null>
    }
    evidencePreview?: QuickVerifyEvidencePreview | null
    rawKey?: string | null
    rawEvidenceId?: string | null
    rawEvidenceHash?: string | null
    rawEvidenceLength?: number | null
  }>
  why?: string
  wafDetected?: boolean
  wafIndicators?: { header?: boolean, body?: boolean, status?: boolean, sources?: string[] }
  suggestions?: string[]
  seededPayloads?: string[]
  payloadAttempts?: Array<{
    payload: string
    url: string
    status: number
    timeMs: number
    diff?: { identical?: boolean, added?: number, removed?: number, changed?: number }
    fingerprintDiff?: { statusChanged?: boolean, lengthDelta?: number, headers?: { added: string[], removed: string[], changed: string[] } }
  }>
  baselineConfidence?: { label: string | null, score: number | null }
  payloadConfirmed?: boolean
  dom?: {
    checked: boolean
    reflected: boolean
    matches: Array<{ selector: string, mode: 'text' | 'attribute', attribute?: string }>
    url?: string
    proof?: { filename: string, path: string }
  }
  remediationSuspected?: boolean
  extraSignals?: Array<{
    type: string
    description: string
    payload?: string
    fingerprintDiff?: { statusChanged?: boolean, lengthDelta?: number, headers?: { added: string[], removed: string[], changed: string[] } }
    contentDiff?: { identical?: boolean, added?: number, removed?: number, changed?: number }
    statusChanged?: boolean
    lenDelta?: number
  }>
  bestPayload?: string | null
  driftCheck?: {
    url?: string
    status?: number
    timeMs?: number
    fingerprintDiff?: { statusChanged?: boolean, lengthDelta?: number, headers?: { added: string[], removed: string[], changed: string[] } }
    contentDiff?: { identical?: boolean, added?: number, removed?: number, changed?: number }
  } | null
  verificationStartedAt?: number
  verificationCompletedAt?: number
  verificationDurationMs?: number | null
  evidence?: QuickVerifyEvidence | null
  rawEvidence?: QuickVerifyEvidenceSummary[]
  consent?: {
    decision: boolean
    preference: QuickVerifyConsentPreference
  }
}

export type QuickVerifyEvidencePreview = {
  status: number | null
  timeMs: number | null
  length: number | null
  hash: string | null
  excerpt?: string | null
  headers?: Record<string, string>
  url?: string | null
  method?: string | null
}

export type QuickVerifySignalResponse = {
  preview?: QuickVerifyEvidencePreview | null
  snapshot?: {
    status?: number | null
    timeMs?: number | null
    length?: number | null
    headers?: Record<string, string>
  } | null
  rawKey?: string | null
  rawEvidenceId?: string | null
  rawEvidenceHash?: string | null
  rawEvidenceLength?: number | null
}

export type QuickVerifyEvidence = {
  baseline?: {
    preview?: QuickVerifyEvidencePreview | null
    rawKey?: string | null
    rawEvidenceId?: string | null
    rawEvidenceHash?: string | null
    rawEvidenceLength?: number | null
  } | null
  signals: {
    boolean: {
      diff?: { identical?: boolean, added?: number, removed?: number, changed?: number } | null
      lengthDelta?: number | null
      fingerprintDiff?: { statusChanged?: boolean, lengthDelta?: number, headers?: { added: string[], removed: string[], changed: string[] } } | null
      responses?: {
        true?: QuickVerifySignalResponse
        false?: QuickVerifySignalResponse
      }
    } | null
    time: {
      deltaMs?: number | null
      fingerprintDiff?: { statusChanged?: boolean, lengthDelta?: number, headers?: { added: string[], removed: string[], changed: string[] } } | null
      responses?: {
        baseline?: QuickVerifySignalResponse
        delayed?: QuickVerifySignalResponse
      }
    } | null
    error: {
      keywordMatch?: boolean
      preview?: QuickVerifyEvidencePreview | null
      rawKey?: string | null
      rawEvidenceId?: string | null
      rawEvidenceHash?: string | null
      rawEvidenceLength?: number | null
    } | null
    payloads: Array<{
      payload: string
      keywordHit?: boolean
      diff?: { identical?: boolean, added?: number, removed?: number, changed?: number } | null
      fingerprintDiff?: { statusChanged?: boolean, lengthDelta?: number, headers?: { added: string[], removed: string[], changed: string[] } } | null
      preview?: QuickVerifyEvidencePreview | null
      rawKey?: string | null
      rawEvidenceId?: string | null
      rawEvidenceHash?: string | null
      rawEvidenceLength?: number | null
    }>
  }
  drift?: {
    preview?: QuickVerifyEvidencePreview | null
    fingerprintDiff?: { statusChanged?: boolean, lengthDelta?: number, headers?: { added: string[], removed: string[], changed: string[] } } | null
    contentDiff?: { identical?: boolean, added?: number, removed?: number, changed?: number } | null
    rawKey?: string | null
    rawEvidenceId?: string | null
    rawEvidenceHash?: string | null
    rawEvidenceLength?: number | null
  } | null
  dom?: {
    ok?: boolean
    reflected?: boolean
    matches?: Array<{ selector: string, mode: string, attribute?: string }>
    url?: string | null
    screenshotCaptured?: boolean
  } | null
  waf?: {
    detected: boolean
    indicators?: { header?: boolean, body?: boolean, status?: boolean, sources?: string[] } | null
  } | null
}

export type AdaptiveTarget = {
  id: string
  hostname: string
  method: string
  token?: string
  verifiedAt?: string | null
  enableParamHarness: boolean
  enablePayloadLab: boolean
  orgId?: string | null
  createdAt?: string
}

const mapAdaptiveTarget = (raw: any): AdaptiveTarget => ({
  id: raw.id,
  hostname: raw.hostname,
  method: raw.method,
  token: raw.token,
  verifiedAt: raw.verified_at ?? raw.verifiedAt ?? null,
  enableParamHarness: Boolean(raw.enable_param_harness ?? raw.enableParamHarness ?? true),
  enablePayloadLab: Boolean(raw.enable_payload_lab ?? raw.enablePayloadLab ?? false),
  orgId: raw.org_id ?? raw.orgId ?? null,
  createdAt: raw.created_at ?? raw.createdAt ?? null
})

export async function listAdaptiveTargets(): Promise<AdaptiveTarget[]> {
  const items = await apiFetch<any[]>('/api/targets')
  if (!Array.isArray(items)) return []
  return items.map(mapAdaptiveTarget)
}

export async function updateAdaptiveTargetControls(
  id: string,
  payload: { enableParamHarness?: boolean; enablePayloadLab?: boolean }
): Promise<AdaptiveTarget> {
  const updated = await apiFetch<any>(`/api/targets/${encodeURIComponent(id)}/adaptive-controls`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  })
  return mapAdaptiveTarget(updated)
}

export type PayloadLabResult = {
  id: string
  payload_lab_run_id: string
  payload_recipe_id?: string | null
  parameter_id?: string | null
  signal?: string | null
  signal_strength?: number | null
  waf_indicator?: boolean | null
  notes?: string | null
  evidence?: Record<string, any> | null
  created_at?: string | null
}

export type PayloadLabRunPreview = {
  curl?: string | null
  request?: {
    method?: string | null
    url?: string | null
    headers?: Record<string, string>
    body?: string | null
  } | null
  response?: {
    status?: number | null
    headers?: Record<string, string | string[]>
    bodyExcerpt?: string | null
  } | null
}

export type PayloadLabRun = {
  id: string
  param_harness_run_id?: string | null
  job_id?: string | null
  parameter_id?: string | null
  payload_recipe_id?: string | null
  payload?: string | null
  target?: string | null
  url?: string | null
  method?: string | null
  status?: string | null
  http_status?: number | null
  response_time_ms?: number | null
  signal_score?: number | null
  signals?: Record<string, any> | null
  waf_indicators?: Record<string, any> | null
  diff_length?: number | null
  reflection_hits?: number | null
  metadata?: Record<string, any> | null
  preview?: PayloadLabRunPreview | null
  created_at?: string | null
  results?: PayloadLabResult[]
}

export type ParamHarnessRun = {
  id: string
  parameter_id?: string | null
  parameter_name?: string | null
  target?: string | null
  url?: string | null
  method?: string | null
  payload?: string | null
  control_value?: string | null
  status_control?: number | null
  status_payload?: number | null
  time_control?: number | null
  time_payload?: number | null
  length_control?: number | null
  length_payload?: number | null
  reflection_hits?: number | null
  diff_length?: number | null
  observation?: string | null
  metadata?: {
    preview_control?: QuickVerifyEvidencePreview | null
    preview_payload?: QuickVerifyEvidencePreview | null
    form_context?: Record<string, any> | null
  }
  created_at?: string | null
  payload_lab?: PayloadLabRun[]
}

export type ParamHarnessRunQuery = {
  parameterId?: string
  target?: string
  reportId?: string
  limit?: number
}

export async function listParamHarnessRuns(query: ParamHarnessRunQuery = {}): Promise<{ runs: ParamHarnessRun[] }> {
  const params = new URLSearchParams()
  if (query.parameterId) params.set('parameterId', query.parameterId)
  if (query.target) params.set('target', query.target)
  if (query.reportId) params.set('reportId', query.reportId)
  if (typeof query.limit === 'number') params.set('limit', String(query.limit))
  const qs = params.toString()
  const url = qs ? `/api/param-harness/runs?${qs}` : '/api/param-harness/runs'
  return apiFetch<{ runs: ParamHarnessRun[] }>(url)
}

export type QuickVerifyEvidenceSummary = {
  id: string | null
  key: string
  scope: string | null
  tag: string | null
  status: number | null
  timeMs: number | null
  bodyHash: string | null
  bodyLength: number | null
  method: string | null
  url: string | null
  createdAt: string | null
  contentType: string | null
  stored: boolean
}

export type QuickVerifyConsentPreference = {
  userId: string
  storeEvidence: boolean | null
  rememberChoice: boolean
  promptSuppressed: boolean
  promptVersion: number
  lastPromptAt: string | null
  lastDecisionAt: string | null
  updatedAt: string | null
  createdAt: string | null
  source: string | null
}

export type QuickVerifyConsentInput = {
  storeEvidence: boolean
  rememberChoice?: boolean
  promptSuppressed?: boolean
  promptVersion?: number
  lastPromptAt?: string
  source?: string
}

export async function verifyFinding(reportId: string, findingId: string, options: { consent?: QuickVerifyConsentInput, captureRawBodies?: boolean } = {}): Promise<VerifyFindingResult> {
  const payload: Record<string, unknown> = { reportId };
  if (options.consent) payload.consent = options.consent;
  if (options.captureRawBodies !== undefined) payload.captureRawBodies = options.captureRawBodies;
  return apiFetch<VerifyFindingResult>(`/api/findings/${encodeURIComponent(findingId)}/verify`, {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}

export async function getQuickVerifyPreference(): Promise<QuickVerifyConsentPreference> {
  const res = await apiFetch<{ ok: boolean, preference: QuickVerifyConsentPreference }>(`/api/quick-verify/preferences`);
  return res.preference;
}

export async function updateQuickVerifyPreference(preference: Partial<QuickVerifyConsentInput> & { storeEvidence?: boolean | null }): Promise<QuickVerifyConsentPreference> {
  const res = await apiFetch<{ ok: boolean, preference: QuickVerifyConsentPreference }>(`/api/quick-verify/preferences`, {
    method: 'POST',
    body: JSON.stringify(preference)
  });
  return res.preference;
}

export async function clearQuickVerifyPreference(): Promise<void> {
  await apiFetch<{ ok: boolean }>(`/api/quick-verify/preferences`, { method: 'DELETE' });
}

export async function listQuickVerifyEvidence(reportId: string, findingId: string, limit = 50): Promise<QuickVerifyEvidenceSummary[]> {
  const res = await apiFetch<{ ok: boolean, evidence: QuickVerifyEvidenceSummary[] }>(
    `/api/reports/${encodeURIComponent(reportId)}/findings/${encodeURIComponent(findingId)}/quick-verify/evidence?limit=${encodeURIComponent(String(limit))}`
  );
  return res.evidence;
}

// User settings & profiles
export type UserScanSettings = {
  user_id: string
  default_profile: string
  defaults: any
  last_used_profile: string | null
  updated_at: string | null
}

export async function getUserScanSettings(): Promise<UserScanSettings> {
  return apiFetch<UserScanSettings>(`/api/user/scan-settings`)
}

export async function updateUserScanSettings(payload: Partial<Pick<UserScanSettings, 'default_profile' | 'defaults' | 'last_used_profile'>>): Promise<UserScanSettings> {
  return apiFetch<UserScanSettings>(`/api/user/scan-settings`, { method: 'PUT', body: JSON.stringify(payload) })
}

export type SafeHostConfigResponse = {
  builtin: string[]
  additional: string[]
  all: string[]
}

export async function getSafeHostnames(): Promise<SafeHostConfigResponse> {
  return apiFetch<SafeHostConfigResponse>(`/api/config/safe-hosts`)
}

export type ServerProfile = { key: string, name: string, description: string, flags: string[] }
export async function getServerSqlmapProfiles(): Promise<ServerProfile[]> {
  return apiFetch<ServerProfile[]>(`/api/sqlmap/profiles`)
}

export type UserProfile = { id: string, user_id: string, name: string, description: string, flags: string[], flagToggles?: Record<string, boolean>, is_custom: boolean, created_at: string, updated_at: string }
export async function listUserProfiles(): Promise<UserProfile[]> {
  return apiFetch<UserProfile[]>(`/api/user/profiles`)
}

export async function createUserProfile(input: { name: string, description?: string, flags: string[], flagToggles?: Record<string, boolean> }): Promise<UserProfile> {
  return apiFetch<UserProfile>(`/api/user/profiles`, { method: 'POST', body: JSON.stringify(input) })
}

export async function updateUserProfile(id: string, input: { name?: string, description?: string, flags?: string[], flagToggles?: Record<string, boolean> }): Promise<UserProfile> {
  return apiFetch<UserProfile>(`/api/user/profiles/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(input) })
}

export async function deleteUserProfile(id: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(`/api/user/profiles/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export type SqlmapValidateResult = {
  ok: boolean
  disallowed: string[]
  warnings: string[]
  normalizedArgs: string[]
  commandPreview: string
  description: string
  impact: { speed: 'low' | 'medium' | 'high'; stealth: 'lower' | 'medium' | 'higher'; exfil: 'low' | 'high' }
}
export async function validateSqlmap(input: { target?: string, profile: string, customFlags?: string, options?: any }): Promise<SqlmapValidateResult> {
  return apiFetch<SqlmapValidateResult>(`/api/sqlmap/validate`, { method: 'POST', body: JSON.stringify(input), suppressUnauthorizedEvent: true })
}

export type ScanPolicy = {
  allowArbitraryVerification: boolean
  enforcePreapprovedTargets: boolean
  disableNonAdminScans: boolean
  updatedAt: string | null
  updatedBy: string | null
}

export type ScanPolicyFlags = Pick<ScanPolicy, 'allowArbitraryVerification' | 'enforcePreapprovedTargets' | 'disableNonAdminScans'>

export async function fetchScanPolicy(): Promise<ScanPolicy> {
  const res = await apiFetch<{ policy: ScanPolicy }>(`/api/admin/scan-policy`)
  return res.policy
}

export async function updateScanPolicyFlags(updates: Partial<ScanPolicyFlags>): Promise<ScanPolicy> {
  const res = await apiFetch<{ policy: ScanPolicy }>(`/api/admin/scan-policy`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  })
  return res.policy
}
